import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Empty,
  Input,
  Popover,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, ReloadOutlined, SearchOutlined, SettingOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { getRequest, postRequest } from "../../util/service";
import DownloadIconButton from "../shared/DownloadIconButton";
import BreadcrumbTrail from "../shared/BreadcrumbTrail";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const RESULTS_TABS = ["glide", "phase", "adme"];

const getStatusProps = (status) => {
  if (!status) return { color: "default", text: "Unknown" };
  const normalized = String(status).replace(/_/g, " ").trim();
  const lower = normalized.toLowerCase().replace(/\s+/g, "");
  let color = "default";

  if (lower.includes("completed") || lower.includes("complete")) color = "green";
  else if (lower.includes("inprogress")) color = "blue";
  else if (lower.includes("failed") || lower.includes("error")) color = "red";
  else if (lower.includes("terminated") || lower.includes("cancelled")) color = "orange";

  return { color, text: normalized.replace(/\b\w/g, (c) => c.toUpperCase()) };
};

const formatPhRange = (ligprep) => {
  const baseRaw = ligprep?.ph ?? ligprep?.ph_range;
  const thresholdRaw = ligprep?.ph_threshold ?? ligprep?.phThreshold;

  const toNum = (value) => (value === undefined || value === null || value === "" ? null : Number(value));
  const baseNum = toNum(baseRaw);
  const thresholdNum = toNum(thresholdRaw);

  const baseDisplay = Number.isFinite(baseNum) ? String(Math.trunc(baseNum)) : (baseRaw ?? null);
  const thresholdDisplay = Number.isFinite(thresholdNum)
    ? thresholdNum.toFixed(1)
    : (thresholdRaw ?? null);

  const hasBase = baseDisplay !== null && baseDisplay !== undefined && baseDisplay !== "";
  const hasThreshold = thresholdDisplay !== null && thresholdDisplay !== undefined && thresholdDisplay !== "";

  if (hasBase && hasThreshold) return `${baseDisplay} +/- ${thresholdDisplay}`;
  if (hasBase) return String(baseDisplay);
  if (hasThreshold) return `+/- ${thresholdDisplay}`;
  return "N/A";
};

const comparePhRange = (a, b) => {
  const leftLigprep = a?.ligprep_input || {};
  const rightLigprep = b?.ligprep_input || {};
  const leftBase = Number(leftLigprep.ph ?? leftLigprep.ph_range);
  const rightBase = Number(rightLigprep.ph ?? rightLigprep.ph_range);
  const leftBaseSafe = Number.isFinite(leftBase) ? leftBase : Number.NEGATIVE_INFINITY;
  const rightBaseSafe = Number.isFinite(rightBase) ? rightBase : Number.NEGATIVE_INFINITY;

  if (leftBaseSafe !== rightBaseSafe) return leftBaseSafe - rightBaseSafe;

  const leftThreshold = Number(leftLigprep.ph_threshold ?? leftLigprep.phThreshold);
  const rightThreshold = Number(rightLigprep.ph_threshold ?? rightLigprep.phThreshold);
  const leftThresholdSafe = Number.isFinite(leftThreshold) ? leftThreshold : Number.POSITIVE_INFINITY;
  const rightThresholdSafe = Number.isFinite(rightThreshold) ? rightThreshold : Number.POSITIVE_INFINITY;

  return leftThresholdSafe - rightThresholdSafe;
};

const deepSearch = (obj, query) => {
  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
    const value = obj[key];

    if (value && typeof value === "object") {
      if (deepSearch(value, query)) return true;
    } else if (String(value).toLowerCase().includes(query)) {
      return true;
    }
  }

  return false;
};

const ALL_COLUMNS_CONFIG = [
  {
    key: "id",
    title: (tab) =>
      tab === "adme" ? "ADME ID" :
      tab === "glide" ? "Docking ID" :
      tab === "phase" ? "Phase ID" :
      "Record ID",
    width: "10%",
    fixed: "left",
    tabs: RESULTS_TABS,
  },
  {
    key: "selectionName",
    title: () => "Selection Name",
    width: "12%",
    sorter: (a, b) => (a.ms_name || "").localeCompare(b.ms_name || ""),
    ellipsis: true,
    tabs: RESULTS_TABS,
  },
  {
    key: "filename",
    title: (tab) => (tab === "glide" ? "Grid File" : "Phase File"),
    ellipsis: true,
    tabs: ["glide", "phase"],
  },
  {
    key: "phThreshold",
    title: () => "pH Range",
    width: "10%",
    sorter: comparePhRange,
    tabs: ["glide", "phase"],
  },
  {
    key: "submittedBy",
    title: () => "Submitted By",
    sorter: (a, b) => (a.submitted_id || "").localeCompare(b.submitted_id || ""),
    tabs: RESULTS_TABS,
  },
  {
    key: "submittedTime",
    title: () => "Submitted Time",
    sorter: (a, b) => dayjs(a.submitted_ts).unix() - dayjs(b.submitted_ts).unix(),
    tabs: RESULTS_TABS,
  },
  {
    key: "status",
    title: () => "Status",
    align: "center",
    width: "12%",
    sorter: (a, b) =>
      String(a.status || a.adme_status || a.job_status || "").localeCompare(
        String(b.status || b.adme_status || b.job_status || "")
      ),
    tabs: RESULTS_TABS,
  },
  {
    key: "download",
    title: () => "Download",
    align: "center",
    width: "10%",
    fixed: "right",
    tabs: ["glide", "phase"],
  },
];

export default function ResultsScreen() {
  const navigate = useNavigate();
  const { projectId, batchId } = useParams();
  const { state } = useLocation();

  const storageKey = `results_active_tab_${projectId}_${batchId}`;
  const defaultFromState = state?.defaultTab && RESULTS_TABS.includes(state.defaultTab)
    ? state.defaultTab
    : null;
  const defaultFromStorage = (() => {
    const saved = sessionStorage.getItem(storageKey);
    return saved && RESULTS_TABS.includes(saved) ? saved : null;
  })();

  const [activeTab, setActiveTab] = useState(defaultFromState || defaultFromStorage || "glide");
  const [loading, setLoading] = useState(false);
  const [glideData, setGlideData] = useState([]);
  const [phaseData, setPhaseData] = useState([]);
  const [admeData, setAdmeData] = useState([]);
  const [glideSearchText, setGlideSearchText] = useState("");
  const [phaseSearchText, setPhaseSearchText] = useState("");
  const [admeSearchText, setAdmeSearchText] = useState("");
  const [visibleColumns, setVisibleColumns] = useState(ALL_COLUMNS_CONFIG.map((column) => column.key));
  const [popoverVisible, setPopoverVisible] = useState(false);

  useEffect(() => {
    if (defaultFromState && defaultFromState !== activeTab) {
      setActiveTab(defaultFromState);
      sessionStorage.setItem(storageKey, defaultFromState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFromState]);

  const onTabChange = (key) => {
    setActiveTab(key);
    sessionStorage.setItem(storageKey, key);
  };

  const fetchResults = useCallback(async () => {
    if (!batchId) return;

    setLoading(true);
    try {
      const [sdResponse, admeResponse] = await Promise.allSettled([
        getRequest(`/riva/sd/${batchId}`),
        getRequest(`/riva/adme/adme-batch/${batchId}`),
      ]);

      if (sdResponse.status === "fulfilled" && sdResponse.value?.data) {
        const glideRows = (sdResponse.value.data.glide || [])
          .sort((a, b) => dayjs(b.submitted_ts).unix() - dayjs(a.submitted_ts).unix());
        const phaseRows = (sdResponse.value.data.phase || [])
          .sort((a, b) => dayjs(b.submitted_ts).unix() - dayjs(a.submitted_ts).unix());
        setGlideData(glideRows);
        setPhaseData(phaseRows);
      } else {
        message.error(sdResponse.value?.message || "Failed to fetch Glide/Phase results.");
        setGlideData([]);
        setPhaseData([]);
      }

      if (admeResponse.status === "fulfilled" && admeResponse.value?.data) {
        const admeRows = (admeResponse.value.data.adme || [])
          .sort((a, b) => dayjs(b.submitted_ts).unix() - dayjs(a.submitted_ts).unix());
        setAdmeData(admeRows);
      } else {
        message.error(admeResponse.value?.message || "Failed to fetch ADME results.");
        setAdmeData([]);
      }
    } catch (err) {
      message.error(err.message || "Failed to load results.");
      setGlideData([]);
      setPhaseData([]);
      setAdmeData([]);
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const filteredGlideData = useMemo(() => {
    if (!glideSearchText) return glideData;
    const query = glideSearchText.toLowerCase();
    return glideData.filter(
      (item) => deepSearch(item, query) || formatPhRange(item.ligprep_input).toLowerCase().includes(query)
    );
  }, [glideData, glideSearchText]);

  const filteredPhaseData = useMemo(() => {
    if (!phaseSearchText) return phaseData;
    const query = phaseSearchText.toLowerCase();
    return phaseData.filter(
      (item) => deepSearch(item, query) || formatPhRange(item.ligprep_input).toLowerCase().includes(query)
    );
  }, [phaseData, phaseSearchText]);

  const filteredAdmeData = useMemo(() => {
    if (!admeSearchText) return admeData;
    const query = admeSearchText.toLowerCase();
    return admeData.filter((item) => deepSearch(item, query));
  }, [admeData, admeSearchText]);

  const handleInputDownload = async (record, usecase) => {
    const fileName = usecase === "glide" ? record.glide_input?.grid_file : record.phase_input?.phase_file;
    if (!fileName) return message.error("No file name available for download.");

    const key = `input-download-${usecase}`;
    message.loading({ content: "Preparing download...", key });

    try {
      const payload = {
        action: "download",
        file_name: fileName,
        file_type: usecase === "glide" ? "Gridfiles" : "Phasefiles",
        process_id: usecase === "glide" ? record.gr_record_id : record.pr_record_id,
        batch_id: record.b_record_id || batchId,
      };
      const resp = await postRequest(`/riva/moleculegenbatches/${projectId}/presigned-url`, payload);
      const url = resp?.data?.url;
      if (!url) throw new Error(resp?.data?.message || "Could not get download link.");

      window.location.href = url;
      message.success({ content: "Download will begin shortly.", key, duration: 2 });
    } catch (err) {
      message.error({ content: err.message || "Download failed.", key, duration: 2 });
    }
  };

  const handleDownload = async (record, usecase) => {
    message.loading({ content: "Preparing download...", key: "download" });

    try {
      const payload = {
        projectId,
        batchId: record.b_record_id || batchId,
        usecaseId: usecase === "glide" ? record.gr_record_id : record.pr_record_id,
        usecase,
      };
      const response = await postRequest("/riva/sd/output", payload);

      if (!response?.data?.url) {
        throw new Error(response?.data?.message || "No result found for this job.");
      }

      const fileName = response?.data?.key || `${(record.gr_record_id || record.pr_record_id || "result")}_output.zip`;
      const link = document.createElement("a");
      link.href = response.data.url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success({ content: "Download Completed!", key: "download", duration: 2 });
    } catch (error) {
      message.error({ content: error.message || "Download failed.", key: "download", duration: 2 });
    }
  };

  const pickAnyId = (record) =>
    record.gr_record_id ||
    record.pr_record_id ||
    record.ar_record_id ||
    record.record_id ||
    record.id;

  const handleIdClick = (record, type) => {
    const navigationState = {
      selectionName: record.ms_name,
      selectionDescription: record.ms_description,
      filterCriteria: record.filter_criteria,
      jobInputs: {
        fileName:
          type === "glide" ? record.glide_input?.grid_file :
          type === "phase" ? record.phase_input?.phase_file :
          null,
        phThreshold: formatPhRange(record.ligprep_input),
        submittedBy: record.submitted_id,
        submittedTime: record.submitted_ts,
        ms_record_id: record.ms_record_id,
      },
    };

    if (type === "glide") {
      navigate(`/project/${projectId}/batch/${batchId}/glide-results/${record.gr_record_id}`, { state: navigationState });
    } else if (type === "phase") {
      navigate(`/project/${projectId}/batch/${batchId}/phase-results/${record.pr_record_id}`, { state: navigationState });
    } else if (type === "adme") {
      navigate(`/project/${projectId}/batch/${batchId}/adme-results/${record.ar_record_id}`, { state: navigationState });
    }
  };

  const getRecordStatus = (record, tab) => {
    if (tab === "adme") {
      return (
        record.status ??
        record.adme_status ??
        record.job_status ??
        (record.is_completed ? "completed" : null) ??
        (record.completed ? "completed" : null) ??
        ""
      );
    }

    return record.status;
  };

  const getColumns = (tab) => {
    const cols = ALL_COLUMNS_CONFIG
      .filter((column) => column.tabs.includes(tab) && visibleColumns.includes(column.key))
      .map((cfg) => {
        const base = {
          key: cfg.key,
          title: cfg.title(tab),
          width: cfg.width,
          ellipsis: cfg.ellipsis,
          align: cfg.align,
          sorter: cfg.sorter,
          fixed: cfg.fixed,
        };

        switch (cfg.key) {
          case "id":
            return {
              ...base,
              render: (_, record) => (
                <Button type="link" onClick={() => handleIdClick(record, tab)}>
                  {pickAnyId(record)}
                </Button>
              ),
            };
          case "selectionName":
            return { ...base, dataIndex: "ms_name" };
          case "filename":
            return {
              ...base,
              render: (_, record) => {
                const fileName =
                  (tab === "glide" ? record.glide_input?.grid_file : record.phase_input?.phase_file) || "N/A";

                return (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Tooltip title={fileName}>
                      <Text style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginRight: 8 }}>
                        {fileName}
                      </Text>
                    </Tooltip>
                    {fileName !== "N/A" && (
                      <DownloadIconButton
                        title={`Download ${fileName}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleInputDownload(record, tab);
                        }}
                      />
                    )}
                  </div>
                );
              },
            };
          case "phThreshold":
            return { ...base, render: (_, record) => formatPhRange(record.ligprep_input) };
          case "submittedBy":
            return { ...base, dataIndex: "submitted_id" };
          case "submittedTime":
            return {
              ...base,
              dataIndex: "submitted_ts",
              render: (timestamp) => (timestamp ? dayjs(timestamp).format("MMM D, YYYY h:mm A") : "N/A"),
            };
          case "status":
            return {
              ...base,
              render: (_, record) => {
                const raw = getRecordStatus(record, tab);
                const { color, text } = getStatusProps(raw);
                return <Tag color={color}>{text}</Tag>;
              },
            };
          case "download":
            return {
              ...base,
              render: (_, record) => {
                const raw = getRecordStatus(record, tab);
                const { text } = getStatusProps(raw);
                const canDownload = /completed|failed/i.test(String(raw || "")) && tab !== "adme";

                return (
                  <DownloadIconButton
                    title="Download Results"
                    disabledTitle={`Cannot download (Status: ${text})`}
                    disabled={!canDownload}
                    onClick={() => handleDownload(record, tab)}
                  />
                );
              },
            };
          default:
            return null;
        }
      })
      .filter(Boolean);

    if (tab === "adme") {
      const evenWidth = `${(100 / (cols.length || 1)).toFixed(2)}%`;
      return cols.map((col) => ({ ...col, width: evenWidth }));
    }

    return cols;
  };

  const columnSelector = (
    <Checkbox.Group
      style={{ display: "flex", flexDirection: "column" }}
      value={visibleColumns}
      onChange={setVisibleColumns}
    >
      {ALL_COLUMNS_CONFIG.filter((column) => column.tabs.includes(activeTab)).map((column) => (
        <Checkbox key={column.key} value={column.key} disabled={column.fixed}>
          {column.title(activeTab)}
        </Checkbox>
      ))}
    </Checkbox.Group>
  );

  const renderTabContent = (type, data, searchText, setSearchText) => (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Row justify="end">
        <Col>
          <Input
            placeholder={`Search ${type} jobs...`}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            style={{ width: 250 }}
            prefix={<SearchOutlined />}
            allowClear
          />
        </Col>
      </Row>
      <Spin spinning={loading}>
        <Table
          dataSource={data}
          columns={getColumns(type)}
          rowKey={(record) =>
            record.gr_record_id ||
            record.pr_record_id ||
            record.ar_record_id ||
            record.record_id ||
            record.id
          }
          pagination={{ pageSize: 10, size: "small" }}
          bordered
          locale={{ emptyText: <Empty description={`No ${type} data available`} /> }}
          scroll={{ x: 1300 }}
        />
      </Spin>
    </Space>
  );

  return (
    <div style={{ padding: 24, background: "#fff", minHeight: "100vh" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, marginTop: 70 }}>
        <Col>
          <Space align="center">
            <Button onClick={() => navigate(-1)} icon={<ArrowLeftOutlined />} />
            <Title level={3} style={{ margin: 0 }}>
              Screening Results
            </Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Popover
              content={columnSelector}
              title="Display Columns"
              trigger="click"
              open={popoverVisible}
              onOpenChange={setPopoverVisible}
            >
              <Button icon={<SettingOutlined />}>Columns</Button>
            </Popover>
            <Button icon={<ReloadOutlined />} onClick={fetchResults} loading={loading}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>
      <BreadcrumbTrail />

      <Tabs activeKey={activeTab} onChange={onTabChange} centered>
        <TabPane tab="Glide Docking" key="glide">
          {renderTabContent("glide", filteredGlideData, glideSearchText, setGlideSearchText)}
        </TabPane>
        <TabPane tab="Pharmacophore Screening" key="phase">
          {renderTabContent("phase", filteredPhaseData, phaseSearchText, setPhaseSearchText)}
        </TabPane>
        <TabPane tab="ADME" key="adme">
          {renderTabContent("adme", filteredAdmeData, admeSearchText, setAdmeSearchText)}
        </TabPane>
      </Tabs>
    </div>
  );
}
